import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Loader } from "@package/subframe/components/Loader";
import {
  FeatherAlertTriangle,
  FeatherCheck,
  FeatherFlame,
  FeatherSnowflake,
} from "@subframe/core";
import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { format } from "date-fns";
import { useCallback, useState } from "react";
import { toast } from "sonner";

import type { OpenHotHoldingAlertResponseDto } from "@/generated/model";
import { useOfflineHotHoldingAlertAck } from "@/hooks/use-offline-hot-holding-alert-ack";
import { useOfflineOpenHotHoldingAlerts } from "@/hooks/use-offline-open-hot-holding-alerts";
import { useOfflineOpenIncidents } from "@/hooks/use-offline-open-incidents";
import {
  ALERT_ACK_ERROR,
  ALERT_ACK_QUEUED,
  ALERT_ACKNOWLEDGED,
} from "@/lib/monitoring/acknowledge-copy";
import { invalidateMonitoringQueries } from "@/lib/monitoring/invalidate-monitoring-queries";

/**
 * Manager-facing alerts widget for the dashboard. Renders two groups alongside
 * each other (NAU-220 AC#1): open out-of-range *fridge* incidents (NAU-219,
 * links to the incident detail page) and open *food* temperature alerts
 * (NAU-220, acknowledge-only inline). Each group stays silent when empty so the
 * dashboard isn't noisy.
 */
interface OpenIncidentsCardProps {
  nurseryId: string;
}

export function OpenIncidentsCard({ nurseryId }: OpenIncidentsCardProps) {
  return (
    <>
      <FridgeAlertsGroup nurseryId={nurseryId} />
      <FoodAlertsGroup nurseryId={nurseryId} />
    </>
  );
}

function FridgeAlertsGroup({ nurseryId }: { nurseryId: string }) {
  const {
    items: incidents,
    isLoading,
    error,
  } = useOfflineOpenIncidents(nurseryId);

  if (isLoading) {
    return (
      <GroupShell>
        <FridgeHeader count={null} />
        <div className="flex items-center justify-center py-6">
          <Loader size="medium" />
        </div>
      </GroupShell>
    );
  }

  if (error) {
    return (
      <GroupShell>
        <FridgeHeader count={null} />
        <span className="text-body font-body text-error-700">
          Failed to load open fridge alerts.
        </span>
      </GroupShell>
    );
  }

  if (incidents.length === 0) return null;

  return (
    <GroupShell highlighted>
      <FridgeHeader count={incidents.length} />
      <div className="flex w-full flex-col gap-3">
        {incidents.map((incident) => {
          const { firstReading } = incident;
          return (
            <Link
              key={incident.id}
              to="/incidents/$incidentId"
              params={{ incidentId: incident.id }}
              className="flex items-start gap-3 rounded-md border border-warning-200 bg-white p-3 hover:border-warning-400 hover:shadow-sm transition"
            >
              <FeatherSnowflake className="mt-0.5 h-5 w-5 flex-none text-warning-800" />
              <div className="flex grow flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {incident.coldStorageName}
                  </span>
                  <Badge variant="warning">{firstReading.temperature}°C</Badge>
                  {firstReading.thresholdMinAtLog !== null &&
                    firstReading.thresholdMaxAtLog !== null && (
                      <span className="text-caption font-caption text-subtext-color">
                        target {firstReading.thresholdMinAtLog}°C to{" "}
                        {firstReading.thresholdMaxAtLog}°C
                      </span>
                    )}
                </div>
                {firstReading.notes && (
                  <span className="text-caption font-caption text-default-font">
                    “{firstReading.notes}”
                  </span>
                )}
                <span className="text-caption font-caption text-subtext-color">
                  Recorded {format(new Date(firstReading.recordedAt), "PPp")}
                </span>
              </div>
              <span className="text-caption font-caption text-brand-700 self-center">
                Review →
              </span>
            </Link>
          );
        })}
      </div>
    </GroupShell>
  );
}

function FoodAlertsGroup({ nurseryId }: { nurseryId: string }) {
  const { items, isLoading, error } = useOfflineOpenHotHoldingAlerts(nurseryId);

  // Optimistically hide rows the manager has acknowledged this session so they
  // disappear instantly whether online or offline (the offline ack hook clears
  // the cache, but the displayed list is only refreshed on the next sync).
  const [acknowledgedIds, setAcknowledgedIds] = useState<Set<string>>(
    () => new Set(),
  );
  const handleAcknowledged = useCallback((alertId: string) => {
    setAcknowledgedIds((prev) => new Set(prev).add(alertId));
  }, []);

  const visible = items.filter((a) => !acknowledgedIds.has(a.id));

  if (isLoading) {
    return (
      <GroupShell>
        <FoodHeader count={null} />
        <div className="flex items-center justify-center py-6">
          <Loader size="medium" />
        </div>
      </GroupShell>
    );
  }

  if (error) {
    return (
      <GroupShell>
        <FoodHeader count={null} />
        <span className="text-body font-body text-error-700">
          Failed to load open food alerts.
        </span>
      </GroupShell>
    );
  }

  if (visible.length === 0) return null;

  return (
    <GroupShell highlighted>
      <FoodHeader count={visible.length} />
      <div className="flex w-full flex-col gap-3">
        {visible.map((alert) => (
          <FoodAlertRow
            key={alert.id}
            alert={alert}
            onAcknowledged={handleAcknowledged}
          />
        ))}
      </div>
    </GroupShell>
  );
}

function FoodAlertRow({
  alert,
  onAcknowledged,
}: {
  alert: OpenHotHoldingAlertResponseDto;
  onAcknowledged: (alertId: string) => void;
}) {
  const { acknowledge, isPending } = useOfflineHotHoldingAlertAck(alert.id);
  const queryClient = useQueryClient();

  const handleClick = async () => {
    let outcome;
    try {
      outcome = await acknowledge();
    } catch {
      toast.error(ALERT_ACK_ERROR);
      return;
    }

    // Say what happened rather than letting the row disappear wordlessly, and
    // never claim success for a write that did not land (NAU-391).
    if (outcome === "failed") {
      toast.error(ALERT_ACK_ERROR);
      return;
    }

    // Before the refresh, not after: the ack hook's `isPending` clears as soon
    // as the write is queued, so a row left up invites a second acknowledge.
    onAcknowledged(alert.id);
    toast.success(outcome === "queued" ? ALERT_ACK_QUEUED : ALERT_ACKNOWLEDGED);
    // The rail drops the row locally, but the dashboard the manager is still
    // sitting on keeps its cached status, and nothing here remounts.
    await invalidateMonitoringQueries(queryClient);
  };

  return (
    <div className="flex items-start gap-3 rounded-md border border-warning-200 bg-white p-3">
      <FeatherFlame className="mt-0.5 h-5 w-5 flex-none text-warning-800" />
      <div className="flex grow flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            {alert.mealName}
          </span>
          <Badge variant="warning">{alert.temperature}°C</Badge>
          <span className="text-caption font-caption text-subtext-color">
            policy floor {alert.thresholdAtLog}°C
          </span>
        </div>
        {alert.notes && (
          <span className="text-caption font-caption text-default-font">
            “{alert.notes}”
          </span>
        )}
        <span className="text-caption font-caption text-subtext-color">
          Recorded {format(new Date(alert.recordedAt), "PPp")}
        </span>
      </div>
      <Button
        variant="brand-secondary"
        size="small"
        icon={<FeatherCheck />}
        loading={isPending}
        disabled={isPending}
        onClick={handleClick}
        className="self-center"
      >
        Acknowledge
      </Button>
    </div>
  );
}

function GroupShell({
  children,
  highlighted = false,
}: {
  children: React.ReactNode;
  highlighted?: boolean;
}) {
  return (
    <div
      className={
        highlighted
          ? "flex w-full flex-col gap-4 rounded-lg border border-warning-300 bg-warning-50 p-4"
          : "flex w-full flex-col gap-3 rounded-lg border border-neutral-200 p-4"
      }
    >
      {children}
    </div>
  );
}

function FridgeHeader({ count }: { count: number | null }) {
  return <AlertsHeader title="Open fridge alerts" count={count} />;
}

function FoodHeader({ count }: { count: number | null }) {
  return <AlertsHeader title="Open food alerts" count={count} />;
}

function AlertsHeader({
  title,
  count,
}: {
  title: string;
  count: number | null;
}) {
  return (
    <div className="flex w-full items-center gap-2">
      <FeatherAlertTriangle className="h-5 w-5 text-warning-800" />
      <span className="text-heading-3 font-heading-3 text-default-font">
        {title}
      </span>
      {count !== null && count > 0 && (
        <Badge variant="warning">
          {count} {count === 1 ? "alert" : "alerts"}
        </Badge>
      )}
    </div>
  );
}
